/**
 * Name styling — the CSS behind a person's chat identity.
 *
 * A name can be flat, a two-stop gradient, glowing, or both. The gradient is
 * painted into the text itself (background-clip), which means the element must
 * not also set a `color`, so everything is resolved in one place and handed
 * back as a ready-to-spread style object.
 */

import type { NameEffect } from "../types";

export interface NameStyleInput {
  /** Primary colour. Already resolved (personal pick or derived fallback). */
  color: string;
  /** Second gradient stop. Falls back to the primary when absent. */
  colorAlt?: string;
  effect?: NameEffect;
}

/**
 * Inline style for a name span. Gradient names paint through the text, so the
 * caller must not layer its own `color` on top.
 */
export function nameStyle({ color, colorAlt, effect }: NameStyleInput): React.CSSProperties {
  const glow = effect === "glow" || effect === "gradient-glow";
  const gradient = effect === "gradient" || effect === "gradient-glow";
  const second = colorAlt || color;

  if (!gradient) {
    return {
      color,
      // A soft halo in the name's own colour, plus a dark rim so it stays
      // legible on light theme backgrounds.
      ...(glow
        ? { textShadow: `0 0 10px ${color}b3, 0 0 22px ${color}66, 0 1px 2px rgba(0,0,0,0.5)` }
        : {}),
    };
  }

  return {
    backgroundImage: `linear-gradient(95deg, ${color}, ${second})`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    // `text-shadow` can't paint under clipped text, so the glow rides on a
    // drop-shadow filter instead.
    ...(glow ? { filter: `drop-shadow(0 0 8px ${color}80) drop-shadow(0 0 16px ${second}59)` } : {}),
  };
}

/**
 * A single colour to use where a gradient can't apply — avatar rings, reply
 * accents, the caret on a quoted message.
 */
export function nameAccent({ color }: NameStyleInput): string {
  return color;
}
